import React, { useCallback, useMemo, useRef } from 'react';
import { useHistory } from "react-router-dom";
import ContextMenu, { Position } from 'devextreme-react/context-menu';
import List from 'devextreme-react/list';
import LoadIndicator from 'devextreme-react/load-indicator';
import { useAuth } from '../../contexts/auth';
import { substringUserName as substr } from './../../utils/string.utils';
import { useReqPopup } from '../../contexts/req-popup';
import notify from "devextreme/ui/notify";
import './user-panel.scss';
import { isAdminRoleChecker, isStaffRoleChecker } from '../../utils/roleChecker';
import { USER_ROLES } from '../../constants/roles';

const SYNC_ALREADY_HINT = 'Синхронизация уже запущена';

const UserPanel = ({ menuMode, setVisibleRefreshOldPeriodPopupFun }) => {
    const {
        user, signOut, syncUserData, loadSyncUser, syncInFlight,
        refreshSyncStatus, dogovors, impersonate
    } = useAuth();
    const { openReqPopup } = useReqPopup();
    const history = useHistory();

    const contextMenuRef = useRef(null);
    const panelRef = useRef(null);
    const syncBusy = !!(loadSyncUser || syncInFlight);
    const panelTarget = menuMode === 'list' ? '.user-panel-list' : '.user-panel-context';

    const hideMenu = useCallback(() => {
        contextMenuRef.current?.instance?.hide();
        if (menuMode === 'list') {
            const menuButton = document.querySelector('.dx-dropdownmenu-button');
            if (menuButton) {
                menuButton.dispatchEvent(new MouseEvent('click', {
                    view: window,
                    bubbles: true,
                    cancelable: true
                }));
            }
        }
    }, [menuMode]);

    const refreshData = useCallback(async () => {
        if (syncBusy) {
            notify(SYNC_ALREADY_HINT, 'warning', 10000);
            return false;
        }
        const res = await syncUserData();
        if (res.isOk) {
            const already = res.message && res.message.indexOf('уже запущена') !== -1;
            notify(res.message, already ? 'warning' : 'success', 10000);
            return true;
        }
        return false;
    }, [syncUserData, syncBusy]);

    const hasStaffAccess = isStaffRoleChecker(user);

    const canReloadPastPeriods = useMemo(() => {
        if (isAdminRoleChecker(user, USER_ROLES.ADMIN)) return true;
        if (!impersonate) return false;
        // при имперсонации current user — потребитель; смотрим исходного админа из панели
        try {
            const original = JSON.parse(window.localStorage.getItem('originalUser'));
            return isAdminRoleChecker(original, USER_ROLES.ADMIN);
        } catch (_) {
            return false;
        }
    }, [user, impersonate]);

    const handlePanelClick = useCallback((e) => {
        if (e.target && typeof e.target.closest === 'function') {
            if (e.target.closest('.dx-context-menu, .user-menu, .dx-menu-item, .dx-list-item')) {
                return;
            }
        }
        if (menuMode !== 'context') {
            if (typeof refreshSyncStatus === 'function') refreshSyncStatus();
            return;
        }
        e.preventDefault();
        e.stopPropagation();

        const instance = contextMenuRef.current?.instance;
        if (!instance) return;

        if (instance.option('visible')) {
            instance.hide();
            return;
        }
        if (typeof refreshSyncStatus === 'function') refreshSyncStatus();
        instance.show();
    }, [menuMode, refreshSyncStatus]);

    const renderMenuItem = useCallback((item) => {
        if (!item) return null;
        const locked = !!(item.lockWhenSync && syncBusy);
        const icon = item.menuIcon || item.icon;
        const cls = [
            'user-menu-item',
            locked ? 'user-menu-item--disabled' : '',
            locked ? 'user-menu-item--syncing' : ''
        ].filter(Boolean).join(' ');
        return (
            <div className={cls}>
                {icon ? <i className={`dx-icon dx-icon-${icon}`} /> : null}
                <span className="user-menu-item__text">{item.text}</span>
                {locked ? (
                    <LoadIndicator
                        className="user-menu-item__loader"
                        width={16}
                        height={16}
                        visible={true}
                    />
                ) : null}
            </div>
        );
    }, [syncBusy]);

    const menuItems = useMemo(() => {
        const items = [
            { key: 'profile', text: 'Профиль', menuIcon: 'user' },
            {
                key: 'request',
                text: 'Новая заявка',
                menuIcon: 'edit',
                visible: process.env.REACT_APP_NAME_ENV !== 'tatenergosbyt'
            },
            {
                key: 'letters',
                text: 'Обращения',
                menuIcon: 'email',
                visible: process.env.REACT_APP_NAME_ENV !== 'tatenergosbyt'
            },
            {
                key: 'refresh',
                text: 'Обновить данные договора',
                menuIcon: 'refresh',
                lockWhenSync: true
            },
            {
                key: 'reload-periods',
                text: 'Перезагрузка прошлых периодов',
                menuIcon: 'event',
                lockWhenSync: true,
                // глобальный Администратор (панель админа), не роль 1 на договоре
                visible: dogovors.length > 0 && canReloadPastPeriods
            },
            {
                key: 'admin',
                text: 'Панель администратора',
                menuIcon: 'file',
                visible: hasStaffAccess
            },
            { key: 'logout', text: 'Выйти', menuIcon: 'runner' }
        ];

        return items.filter((item) => item.visible !== false);
    }, [dogovors, hasStaffAccess, canReloadPastPeriods]);

    const onItemClick = useCallback((e) => {
        const key = e?.itemData?.key;
        if (!key) return;
        if (e.event) {
            e.event.stopPropagation();
            e.event.preventDefault();
        }

        if (e.itemData.lockWhenSync && syncBusy) {
            notify(SYNC_ALREADY_HINT, 'warning', 10000);
            return;
        }

        switch (key) {
            case 'profile':
                hideMenu();
                history.push('/profile');
                break;
            case 'request':
                hideMenu();
                if (dogovors.length > 0) openReqPopup(true, 1, null, [], [], false);
                else openReqPopup(true, 3);
                break;
            case 'letters':
                hideMenu();
                history.push('/letters');
                break;
            case 'refresh':
                hideMenu();
                refreshData();
                break;
            case 'reload-periods':
                hideMenu();
                setVisibleRefreshOldPeriodPopupFun(true);
                break;
            case 'admin':
                hideMenu();
                history.push('/admin-panel');
                break;
            case 'logout':
                hideMenu();
                signOut();
                break;
            default:
                break;
        }
    }, [
        syncBusy, hideMenu, history, dogovors, openReqPopup,
        refreshData, setVisibleRefreshOldPeriodPopupFun, signOut
    ]);

    return (
        <div>
            <div
                ref={panelRef}
                className={`user-panel ${menuMode === 'list' ? 'user-panel-list' : 'user-panel-context'}`}
                onClick={handlePanelClick}
            >
                <div className="user-info">
                    <div className={`image-container${syncBusy ? ' image-container--syncing' : ''}`}>
                        {syncBusy ? (
                            <LoadIndicator className="user-image-sync" width={22} height={22} visible={true} />
                        ) : (
                            <div style={{ backgroundSize: 'cover' }} className="user-image">
                                {substr(user)}
                            </div>
                        )}
                    </div>
                    <div className="user-name">
                        {user.firstName ? user.firstName + ' ' + user.lastName : user.lastName}
                    </div>
                </div>
            </div>

            {menuMode === 'context' && (
                <ContextMenu
                    ref={contextMenuRef}
                    items={menuItems}
                    target={panelTarget}
                    showEvent="none"
                    hideOnOutsideClick={(e) => {
                        if (e && e.target && typeof e.target.closest === 'function') {
                            if (e.target.closest('.user-panel-context')) return false;
                        }
                        return true;
                    }}
                    cssClass="user-menu"
                    itemRender={renderMenuItem}
                    onItemClick={onItemClick}
                >
                    <Position my="top center" at="bottom center" offset="0 8" />
                </ContextMenu>
            )}

            {menuMode === 'list' && (
                <List
                    className="dx-toolbar-menu-action"
                    items={menuItems}
                    itemRender={renderMenuItem}
                    onItemClick={onItemClick}
                />
            )}
        </div>
    );
};

export default UserPanel;
